'use client';
import React from 'react';

import { SessionContext } from '@benzinga/session-context';
import { AuthenticationManager, User } from '@benzinga/session';
import Hooks from '@benzinga/hooks';
import { UserManager, UserManagerEvent } from '@benzinga/user-manager';
import { appEnvironment, appName } from '@benzinga/utils';

export const useUser = (): User | null => {
  const session = React.useContext(SessionContext);
  const [user, setUser] = React.useState<User | null>(null);

  // this will only debounce in the current component it was referenced
  // debounce will not be shared across components
  // multiple calls in different components will have different debounce timers
  const debounceGetAuthSession = React.useCallback(() => {
    const auth = session.getManager(AuthenticationManager).getAuthentication();

    if (auth && auth.user) {
      setUser(auth.user);
    } else {
      session
        .getManager(AuthenticationManager)
        .getAuthSession()
        .then(authSession => {
          if (authSession?.ok?.user) {
            setUser(authSession.ok.user);
          }
        });
    }
  }, [session]);

  React.useEffect(() => {
    const isProtoApp = appEnvironment().isApp(appName.proto);
    if (!isProtoApp) {
      debounceGetAuthSession();
    }
  }, [debounceGetAuthSession]);

  React.useEffect(() => {
    const user = session.getManager(UserManager).getUser();
    if (user) {
      setUser(user);
    }
  }, [session]);

  Hooks.useSubscriber(session.getManager(UserManager), (event: UserManagerEvent) => {
    switch (event.type) {
      case 'user:user_update':
        if (event.user) {
          setUser(event.user);
        } else {
          setUser(null);
        }
    }
  });

  return user;
};
